/**
 * 판매몰 실시간 검색 결과를 **랭킹 검색과 같은 좁히기로** 거르고 세는 규칙 — 상품 화면(`useMarketResultsModel`)과
 * 홈 답 카드(`ask.answer` 상품 갈래)가 같은 이 함수로 「조건에 맞는 전체 상품 수 = 랭킹 검색 수 + 실시간분」을 낸다.
 *
 * ```
 *   liveOnlyProducts     랭킹에 이어진 것(ranked)·이미 받은 랭킹 결과와 같은 상품을 뺀다(랭킹 쪽을 채택)
 *   narrowLiveProducts   종류·상세수집·태그가 걸리면 0(실시간분엔 랭킹·상세가 없다) · 지역·채널·브랜드·가격대는 같은 기준으로 거른다
 * ```
 *
 * ⚠️ `'use client'` 를 붙이지 않는다 — 서버(답 카드)도 부른다.
 */

import { normalizeBrandKey } from 'src/utils/brand/brand-alias-groups';

import { OVERSEAS_PRODUCT_SOURCE_IDS } from 'src/constants/product-sources';
import { isPriceInBand, findProductPriceBandSpec } from 'src/constants/product-price-bands';

/** 거르는 데 쓰는 상품의 값 — 랭킹 결과·실시간 결과 어느 쪽 모양이든 이만큼은 있다 */
export type LiveNarrowProduct = {
  source?: string | null;
  postId?: string | null;
  /** 실시간 검색으로 들어온 상품의 몰 ID */
  liveId?: string | null;
  brand?: string | null;
  displayPrice?: number | null;
  /** 랭킹 색인과 이어졌나 — 이어진 것은 랭킹 검색이 이미 센다 */
  ranked?: boolean;
};

/** 화면이 걸어 둔 좁히기 — 빈 값은 그 축을 안 건다 */
export type LiveNarrowState = {
  searchKind: string;
  detailOnly: boolean;
  activeTagCount: number;
  priceBand: string;
  resultSource: string;
  /** 브랜드 칩의 그룹 열쇠(`normalizeBrandKey`) */
  brand: string;
  /** `'domestic'` | `'overseas'` | 빈 값 */
  region: string;
};

/** 패싯 규약의 축 이름 — 칩 수를 셀 때 **자기 축을 뺀 집합**에서 센다 */
export type LiveNarrowAxis = 'kind' | 'detail' | 'tags' | 'price' | 'source' | 'brand' | 'region';

/**
 * 상세 수집 API 가 받는 **몰 상품 ID** — 실시간분은 `liveId`, 화해 랭킹 문서는 `postId` 의 접두사(`2_`)를 뗀다.
 */
export function productMallId(product: LiveNarrowProduct | null | undefined): string {
  if (product?.liveId) return product.liveId;
  const postId = String(product?.postId || '');
  if (product?.source === 'hwahae') return postId.replace(/^\d+_/, '');
  return postId;
}

/** @returns 랭킹과 이어지지 않았고 받은 랭킹 결과(`dbItems`)에도 없는 실시간분 */
export function liveOnlyProducts<T extends LiveNarrowProduct>(dbItems: readonly LiveNarrowProduct[], liveItems: readonly T[]): T[] {
  const keyOf = (item: LiveNarrowProduct) => `${item.source}:${productMallId(item)}`;
  const seen = new Set(dbItems.map(keyOf));
  return liveItems.filter((item) => !item.ranked && !seen.has(keyOf(item)));
}

/**
 * @param exceptAxis 이 축은 걸지 않는다(패싯 칩 수) · `null` 이면 전 축 — 화면에 그려지는 것
 * @returns 좁히기를 통과한 실시간분
 */
export function narrowLiveProducts<T extends LiveNarrowProduct>(liveOnly: readonly T[], narrow: LiveNarrowState, exceptAxis: LiveNarrowAxis | null): T[] {
  const { searchKind, detailOnly, activeTagCount, priceBand, resultSource, brand, region } = narrow;
  if (exceptAxis !== 'kind' && searchKind) return [];
  if (exceptAxis !== 'detail' && detailOnly) return [];
  if (exceptAxis !== 'tags' && activeTagCount) return [];
  const band = exceptAxis === 'price' ? null : findProductPriceBandSpec(priceBand);
  return liveOnly.filter((item) => {
    // ⚠️ 지역은 항목마다 본다 — 실시간분에 아마존이 섞여 있어 통째로 빼면 「글로벌」에서 아마존분까지 사라진다
    if (exceptAxis !== 'region' && region) {
      const itemRegion = OVERSEAS_PRODUCT_SOURCE_IDS.has(String(item.source ?? '')) ? 'overseas' : 'domestic';
      if (itemRegion !== region) return false;
    }
    if (exceptAxis !== 'source' && resultSource && item.source !== resultSource) return false;
    // ⚠️ 이름이 아니라 그룹 열쇠로 견준다 — 칩이 표기 변형을 묶어 놓았다
    if (exceptAxis !== 'brand' && brand && normalizeBrandKey(item.brand) !== brand) return false;
    if (band) {
      const price = Number(item.displayPrice);
      if (!(price > 0) || !isPriceInBand(band, price)) return false;
    }
    return true;
  });
}
